import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import {
  Button,
  Card,
  Checkbox,
  Cluster,
  EmptyState,
  Grid,
  Input,
  Modal,
  Select,
  Spinner,
  Stack,
  Tabs,
  Textarea,
  useToast,
} from "@salesapp/ui";
import { leadGenApi, type CompanyMatch, type LeadGenProfile, type LeadGenResult, type LeadGenRun, type LeadGenStatus } from "../../lib/leadGenApi";
import type { LeadInput } from "../../lib/crmApi";
import { countryName } from "../../lib/countries";
import { useCrmHref } from "../../lib/customerUrl";
import { LOCALE_CHANGED_EVENT, useLocale } from "../../shell/LocaleProvider";
import { LeadCaptureForm } from "./LeadCaptureForm";
import { ListWorkspace } from "./RecordWorkspace";
import { ResponsiveActions } from "./ResponsiveActions";
import { SearchBar } from "./SearchBar";

const EMPTY_PROFILE: LeadGenProfile = {
  business_description: "",
  target_description: "",
  include_website: false,
  postcode: "",
};
const POLL_MS = 3000;
const SETTINGS_TIP = "Country is in Settings → Territories & Locale. Company website is in Settings → Company Info.";

/** Only the gaps that are still open. A saved country must not keep reading as if it were missing. */
function setupReasons(missing: string[]): string {
  return [
    missing.includes("country") ? "Postcodes are searched in your country." : "",
    missing.includes("company website")
      ? "Your website lets AI rewrite describe you and keeps your own business off the list."
      : "",
  ]
    .filter(Boolean)
    .join(" ");
}

/** Exactly the fields the API accepts: it rejects unknown ones, and the saved profile carries updated_at. */
function profileBody(profile: LeadGenProfile): LeadGenProfile {
  return {
    business_description: profile.business_description,
    target_description: profile.target_description,
    include_website: profile.include_website,
    postcode: profile.postcode,
  };
}

function isActive(run: LeadGenRun | null | undefined): boolean {
  return Boolean(run && (run.status === "queued" || run.status === "running"));
}

function websiteHref(url: string | null): string | null {
  if (!url) return null;
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

function websiteLabel(url: string | null): string {
  return (url ?? "").replace(/^https?:\/\/(www\.)?/i, "").replace(/\/+$/, "");
}

// --- sortable, filterable tables -----------------------------------------------

type SortDir = "asc" | "desc";
type Column = { key: string; label: string; value: (row: LeadGenResult) => string | number | null; title?: string };

function compareValues(a: string | number | null, b: string | number | null): number {
  if (a === b) return 0;
  if (a === null || a === "") return 1; // blanks always last
  if (b === null || b === "") return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

function useSortedFiltered(rows: LeadGenResult[], columns: Column[], initial: { key: string; dir: SortDir }) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState(initial);
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = needle
      ? rows.filter((row) =>
          columns.some((column) => String(column.value(row) ?? "").toLowerCase().includes(needle)),
        )
      : rows;
    const column = columns.find((item) => item.key === sort.key) ?? columns[0];
    const direction = sort.dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = column.value(a);
      const bv = column.value(b);
      if (av === null || av === "" || bv === null || bv === "") return compareValues(av, bv);
      return compareValues(av, bv) * direction;
    });
  }, [rows, columns, query, sort]);
  const toggle = (key: string) =>
    setSort((current) => (current.key === key ? { key, dir: current.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  return { query, setQuery, sort, toggle, visible };
}

function SortHeader({ column, sort, onSort }: { column: Column; sort: { key: string; dir: SortDir }; onSort: (key: string) => void }) {
  const active = sort.key === column.key;
  return (
    <th aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"} title={column.title}>
      <button type="button" className="crm-sort-btn" onClick={() => onSort(column.key)}>
        {column.label}
      </button>
    </th>
  );
}

const time = (value: string | null) => (value ? Date.parse(value) || 0 : null);

/** The scoring prompt's own bands: 70+ clear target, 40-69 plausible, below 40 poor. */
function fitBand(score: number): { label: string; badge: string } {
  if (score >= 70) return { label: "Strong", badge: "crm-badge crm-badge--active" };
  if (score >= 40) return { label: "Good", badge: "crm-badge crm-badge--issued" };
  return { label: "Weak", badge: "crm-badge" };
}

function FitCell({ score }: { score: number | null }) {
  if (score === null) return <>—</>;
  const band = fitBand(score);
  return (
    <>
      <strong>{score}</strong>{" "}
      <span className={band.badge} data-testid="leadgen-fit-band">
        {band.label}
      </span>
    </>
  );
}

const PENDING_COLUMNS: Column[] = [
  { key: "name", label: "Business", value: (row) => `${row.name} ${row.category ?? ""} ${row.address ?? ""}` },
  { key: "phone", label: "Phone", value: (row) => row.phone },
  { key: "email", label: "Email", value: (row) => row.email },
  { key: "website", label: "Website", value: (row) => websiteLabel(row.website) },
  {
    key: "fit",
    label: "Fit",
    value: (row) => row.fit_score,
    title:
      "How well this business fits what you sell, 0-100: Strong 70+, Good 40-69, Weak below 40. Higher when a recent public signal matches your services.",
  },
  {
    key: "comment",
    label: "Comment",
    value: (row) => `${row.fit_reason ?? ""} ${row.call_opener ?? ""}`.trim(),
    title:
      "Call note: a planned opening, a move, hiring, or a public complaint, then which of your services to qualify. Opener: a first sentence to say on the call.",
  },
  { key: "generated", label: "Generated", value: (row) => time(row.created_at) },
];

const HISTORY_COLUMNS: Column[] = [
  { key: "name", label: "Business", value: (row) => `${row.name} ${row.category ?? ""}` },
  { key: "phone", label: "Phone", value: (row) => row.phone },
  { key: "email", label: "Email", value: (row) => row.email },
  { key: "outcome", label: "Outcome", value: (row) => (row.status === "valid" ? "Valid" : "Invalid") },
  { key: "by", label: "Marked by", value: (row) => row.processed_by_email ?? null },
  { key: "marked", label: "Marked", value: (row) => time(row.processed_at) },
  { key: "generated", label: "Generated", value: (row) => time(row.created_at) },
];

type ConvertTarget = { result: LeadGenResult; company: CompanyMatch | null };

export function LeadGeneratorPage({ orgId }: { orgId: string }) {
  const { show } = useToast();
  const { formatDateTime } = useLocale();
  const crmHref = useCrmHref();
  const [status, setStatus] = useState<LeadGenStatus | null>(null);
  const [profile, setProfile] = useState<LeadGenProfile>(EMPTY_PROFILE);
  const [countText, setCountText] = useState("10");
  const [pending, setPending] = useState<LeadGenResult[]>([]);
  const [history, setHistory] = useState<LeadGenResult[]>([]);
  const [outcome, setOutcome] = useState<"all" | "valid" | "invalid">("all");
  const [tab, setTab] = useState<"pending" | "history">("pending");
  const [run, setRun] = useState<LeadGenRun | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rewriting, setRewriting] = useState(false);
  const [starting, setStarting] = useState(false);
  const [busyRow, setBusyRow] = useState<string | null>(null);
  const [draftingId, setDraftingId] = useState<string | null>(null);
  const [convertTarget, setConvertTarget] = useState<ConvertTarget | null>(null);
  const [emailDraft, setEmailDraft] = useState<{ business: string; subject: string; body: string } | null>(null);
  const pollRef = useRef<number | null>(null);

  const canConfigure = Boolean(status?.can_configure);
  const pendingTable = useSortedFiltered(pending, PENDING_COLUMNS, { key: "fit", dir: "desc" });
  const historyRows = useMemo(
    () => (outcome === "all" ? history : history.filter((row) => row.status === outcome)),
    [history, outcome],
  );
  const historyTable = useSortedFiltered(historyRows, HISTORY_COLUMNS, { key: "marked", dir: "desc" });

  const loadResults = useCallback(async (withHistory: boolean) => {
    const rows = await leadGenApi.pending(orgId);
    setPending(rows.results);
    if (withHistory) setHistory((await leadGenApi.history(orgId)).results);
  }, [orgId]);

  const loadAll = useCallback(async () => {
    try {
      const next = await leadGenApi.status(orgId);
      setStatus(next);
      setRun(next.latest_run);
      if (next.profile) setProfile({ ...EMPTY_PROFILE, ...next.profile });
      await loadResults(next.can_configure);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Lead generation could not be loaded.");
    }
  }, [orgId, loadResults]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  // Settings live on another screen. Re-read country and website when this tab
  // is shown again, or as soon as App settings are saved, without wiping the
  // descriptions typed on this page.
  const refreshSetup = useCallback(async () => {
    try {
      const next = await leadGenApi.status(orgId);
      setStatus((current) =>
        current
          ? {
              ...current,
              country: next.country,
              website: next.website,
              monthly_limit: next.monthly_limit,
              used_this_month: next.used_this_month,
              remaining: next.remaining,
              ai_enabled: next.ai_enabled,
              can_configure: next.can_configure,
            }
          : current,
      );
    } catch {
      // Keep the screen already shown. The next visit loads it again.
    }
  }, [orgId]);

  useEffect(() => {
    let timer: number | null = null;
    const schedule = () => {
      if (document.visibilityState === "hidden") return;
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = null;
        void refreshSetup();
      }, 0);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") schedule();
    };
    window.addEventListener("focus", schedule);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener(LOCALE_CHANGED_EVENT, schedule);
    return () => {
      if (timer) window.clearTimeout(timer);
      window.removeEventListener("focus", schedule);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener(LOCALE_CHANGED_EVENT, schedule);
    };
  }, [refreshSetup]);

  // Poll the active search until the worker finishes it.
  useEffect(() => {
    if (!isActive(run) || !run) return;
    pollRef.current = window.setTimeout(() => {
      leadGenApi
        .run(orgId, run.id)
        .then((next) => {
          if (isActive(next)) {
            setRun(next);
            return;
          }
          setRun(next);
          if (next.status === "succeeded" && next.created_count) {
            show(`${next.created_count} new ${next.created_count === 1 ? "business" : "businesses"} to call.`, "success");
          }
          void loadAll();
        })
        .catch(() => setRun({ ...run }));
    }, POLL_MS);
    return () => {
      if (pollRef.current) window.clearTimeout(pollRef.current);
    };
  }, [orgId, run, loadAll, show]);

  // A typed number above what is left this month cannot be generated, so bring the box down to the remainder.
  useEffect(() => {
    if (!status || status.remaining <= 0) return;
    setCountText((current) => {
      const n = Number.parseInt(current, 10);
      if (Number.isFinite(n) && n > status.remaining) return String(status.remaining);
      return current;
    });
  }, [status]);

  const update = (patch: Partial<LeadGenProfile>) => setProfile((current) => ({ ...current, ...patch }));

  async function rewrite() {
    setRewriting(true);
    setError(null);
    try {
      const next = await leadGenApi.rewrite(orgId, profileBody(profile));
      setProfile({ ...profile, ...next });
      show("Descriptions rewritten and saved. Check them before you generate.", "success");
    } catch (err) {
      setError(err instanceof Error ? err.message : "AI rewrite failed.");
    } finally {
      setRewriting(false);
    }
  }

  const asked = Number.parseInt(countText, 10);
  const leadCount = Number.isFinite(asked) ? asked : 0;

  async function generate() {
    setStarting(true);
    setError(null);
    try {
      setRun(await leadGenApi.generate(orgId, { ...profileBody(profile), count: leadCount }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "The search could not start.");
    } finally {
      setStarting(false);
    }
  }

  async function draftEmail(row: LeadGenResult) {
    setDraftingId(row.id);
    try {
      const draft = await leadGenApi.email(orgId, row.id);
      setEmailDraft({ business: row.name, subject: draft.subject, body: draft.body });
    } catch (err) {
      show(err instanceof Error ? err.message : "The email could not be written.", "danger");
    } finally {
      setDraftingId(null);
    }
  }

  async function copyEmail() {
    if (!emailDraft) return;
    const text = `Subject: ${emailDraft.subject}\n\n${emailDraft.body}`;
    try {
      await navigator.clipboard.writeText(text);
      show("Copied. Paste it into your email.", "success");
    } catch {
      show("Select the email and copy it.", "danger");
    }
  }

  async function markInvalid(row: LeadGenResult) {
    setBusyRow(row.id);
    try {
      await leadGenApi.invalid(orgId, row.id);
      await loadResults(canConfigure);
    } catch (err) {
      show(err instanceof Error ? err.message : "Could not mark this record invalid.", "danger");
      await loadResults(canConfigure);
    } finally {
      setBusyRow(null);
    }
  }

  async function openConvert(row: LeadGenResult) {
    setBusyRow(row.id);
    try {
      const match = await leadGenApi.companyMatch(orgId, row.id);
      setConvertTarget({ result: match.result, company: match.company });
    } catch (err) {
      show(err instanceof Error ? err.message : "This record could not be opened.", "danger");
      await loadResults(canConfigure);
    } finally {
      setBusyRow(null);
    }
  }

  async function submitConvert(body: LeadInput) {
    if (!convertTarget) throw new Error("No record selected.");
    const res = await leadGenApi.convert(orgId, convertTarget.result.id, body);
    return res.lead;
  }

  if (!status) {
    return error ? <p className="crm-error">{error}</p> : <Spinner />;
  }

  const active = isActive(run);
  const usage = `${status.used_this_month} of ${status.monthly_limit} used this month`;
  const missingSetup = [!status.country && "country", !status.website && "company website"].filter(Boolean) as string[];
  const canGenerate =
    status.ai_enabled &&
    status.remaining > 0 &&
    leadCount >= 1 &&
    leadCount <= status.remaining &&
    missingSetup.length === 0 &&
    !active &&
    !starting &&
    Boolean(profile.target_description.trim() && profile.postcode.trim());

  const runMessage = (() => {
    if (!run) return null;
    if (active) return { tone: "muted", text: `Searching ${run.postcode} for new businesses…` };
    if (run.status === "failed") return { tone: "error", text: run.error_message || "The search failed. Try again." };
    if (run.error_code === "no_new_results") {
      return { tone: "muted", text: "No new businesses found here. Select a new target area to search." };
    }
    return null;
  })();

  return (
    <ListWorkspace title="LeadGen" actions={<span className="crm-muted" data-testid="leadgen-usage">{usage}</span>}>
      {error && <p className="crm-error">{error}</p>}
      {canConfigure && (
        <Card>
          <Stack gap={4}>
            <h2 className="crm-section-title">Find businesses to call</h2>
            {!status.ai_enabled && (
              <p className="crm-error">AI is turned off for your organization. Contact Saleslox support to enable it.</p>
            )}
            {missingSetup.length > 0 && (
              <p className="crm-error" role="alert" data-testid="leadgen-setup">
                Before you can generate, add your {missingSetup.join(" and ")} in{" "}
                {!status.country && <Link to={crmHref("/settings?tab=locale")}>Territories & Locale</Link>}
                {!status.country && !status.website ? " and " : ""}
                {!status.website && <Link to={crmHref("/settings?tab=company")}>Company Info</Link>}
                . {setupReasons(missingSetup)}
              </p>
            )}
            <Grid columns={2} gap={4} className="leadgen-descriptions">
              <Textarea
                label="Description of your business"
                tooltip="What you sell and what makes you different. The AI uses this to judge which businesses fit."
                rows={5}
                value={profile.business_description}
                onChange={(event) => update({ business_description: event.target.value })}
              />
              <Textarea
                label="Description of the target customers"
                tooltip="The kind of business you want to call: industry, size, and anything that makes one a good fit."
                rows={5}
                value={profile.target_description}
                onChange={(event) => update({ target_description: event.target.value })}
              />
            </Grid>
            <Cluster gap={3} align="center">
              <Checkbox
                label="Include my website"
                tooltip={`AI rewrite also reads your company website and merges what it finds with the descriptions above. ${SETTINGS_TIP}`}
                checked={profile.include_website && Boolean(status.website)}
                disabled={!status.website}
                onChange={(event) => update({ include_website: event.target.checked })}
              />
              <span className="crm-muted" data-testid="leadgen-website" title={SETTINGS_TIP}>
                {status.website ? `Website: ${websiteLabel(status.website)}` : "No company website set."}{" "}
                <Link to={crmHref("/settings?tab=company")}>{status.website ? "Change" : "Add it"}</Link>
              </span>
              <Button
                variant="secondary"
                onClick={() => void rewrite()}
                disabled={rewriting || !status.ai_enabled}
                title="Rewrite both descriptions so the search finds better matches. You can edit the result."
              >
                {rewriting ? "Rewriting…" : "AI rewrite"}
              </Button>
            </Cluster>
            <Cluster gap={3} align="end">
              <Input
                label="Postcode"
                tooltip="The area to search. Businesses in this postcode are listed first, then nearby ones."
                value={profile.postcode}
                onChange={(event) => update({ postcode: event.target.value })}
              />
              <Input
                label="Country"
                tooltip={`Your organization's country. Postcodes are searched in this country. ${SETTINGS_TIP}`}
                value={status.country ? countryName(status.country) : "Not set"}
                readOnly
                disabled
              />
              <div className="leadgen-count">
                <Input
                  label="Leads"
                  type="number"
                  min={1}
                  max={Math.max(status.remaining, 1)}
                  inputMode="numeric"
                  tooltip="How many to find this time. What is left of this month's allowance stays available for another postcode."
                  value={countText}
                  onChange={(event) => setCountText(event.target.value)}
                  data-testid="leadgen-count"
                />
              </div>
              <Button
                onClick={() => void generate()}
                disabled={!canGenerate}
                data-testid="leadgen-generate"
                title={missingSetup.length ? `Add your ${missingSetup.join(" and ")} in Settings first.` : undefined}
              >
                {active || starting ? "Generating…" : "Generate"}
              </Button>
            </Cluster>
            {status.remaining <= 0 && (
              <p className="crm-muted">This month&rsquo;s allowance of {status.monthly_limit} is used. It resets on the 1st.</p>
            )}
            {runMessage && (
              <Cluster gap={2} align="center" role="status">
                {active && <Spinner />}
                <span className={runMessage.tone === "error" ? "crm-error" : "crm-muted"}>{runMessage.text}</span>
              </Cluster>
            )}
          </Stack>
        </Card>
      )}

      {canConfigure && (
        <Tabs
          items={[
            { key: "pending", label: `To call (${pending.length})` },
            { key: "history", label: "History" },
          ]}
          activeKey={tab}
          onChange={(next) => setTab(next as "pending" | "history")}
        />
      )}

      {(tab === "pending" || !canConfigure) &&
        (pending.length === 0 ? (
          <Card>
            <EmptyState
              title="Nothing to call yet"
              description={
                canConfigure
                  ? "Describe your target customers, pick a postcode, and press Generate."
                  : "Your admin generates new businesses to call. They will appear here."
              }
            />
          </Card>
        ) : (
          <Stack gap={3}>
            <div className="crm-toolbar">
              <SearchBar
                value={pendingTable.query}
                onChange={pendingTable.setQuery}
                placeholder="Filter businesses to call…"
                label="Filter businesses to call"
                hint="Matches business name, category, address, phone, email, website, or the comment. Click a column heading to sort."
              />
            </div>
            <Card className="crm-table-card">
              <table className="crm-table" aria-label="Businesses to call">
                <thead>
                  <tr>
                    {PENDING_COLUMNS.map((column) => (
                      <SortHeader key={column.key} column={column} sort={pendingTable.sort} onSort={pendingTable.toggle} />
                    ))}
                    <th className="crm-table-actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingTable.visible.length === 0 && (
                    <tr>
                      <td colSpan={PENDING_COLUMNS.length + 1} className="crm-muted">No businesses match this filter.</td>
                    </tr>
                  )}
                  {pendingTable.visible.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <strong>{row.name}</strong>
                        <div className="crm-muted">{[row.category, row.address].filter(Boolean).join(", ")}</div>
                      </td>
                      <td className="leadgen-nowrap">
                        <a href={`tel:${row.phone.replace(/[^\d+]/g, "")}`}>{row.phone}</a>
                      </td>
                      <td>
                        <a href={`mailto:${row.email}`}>{row.email}</a>
                      </td>
                      <td>
                        {websiteHref(row.website) ? (
                          <a href={websiteHref(row.website) ?? undefined} target="_blank" rel="noopener noreferrer">
                            {websiteLabel(row.website)}
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="leadgen-nowrap">
                        <FitCell score={row.fit_score} />
                      </td>
                      <td>
                        {row.fit_reason || "—"}
                        {row.call_opener && (
                          <div className="crm-muted" data-testid="leadgen-opener">
                            Opener: &ldquo;{row.call_opener}&rdquo;
                          </div>
                        )}
                      </td>
                      <td>{formatDateTime(row.created_at)}</td>
                      <td className="crm-table-actions">
                        <ResponsiveActions
                          primary={
                            <Button
                              disabled={busyRow === row.id || draftingId === row.id}
                              onClick={() => void openConvert(row)}
                              title="Add this business as a lead."
                            >
                              Valid
                            </Button>
                          }
                          more={
                            <>
                              {status?.ai_enabled && (
                                <Button
                                  variant="secondary"
                                  disabled={busyRow === row.id || draftingId === row.id}
                                  onClick={() => void draftEmail(row)}
                                  aria-label={`Draft email for ${row.name}`}
                                  title="Write a first email you can copy, from your sales pitch and this business."
                                >
                                  {draftingId === row.id ? "Writing…" : "Email"}
                                </Button>
                              )}
                              <Button
                                variant="secondary"
                                disabled={busyRow === row.id || draftingId === row.id}
                                onClick={() => void markInvalid(row)}
                                title="Not a fit or not reachable. Moves the record to history."
                              >
                                Invalid
                              </Button>
                            </>
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </Stack>
        ))}

      {canConfigure && tab === "history" &&
        (history.length === 0 ? (
          <Card>
            <EmptyState title="No history yet" description="Records marked valid or invalid appear here." />
          </Card>
        ) : (
          <Stack gap={3}>
            <div className="crm-toolbar">
              <SearchBar
                value={historyTable.query}
                onChange={historyTable.setQuery}
                placeholder="Filter history…"
                label="Filter history"
                hint="Matches business, phone, email, outcome, or who marked it. Click a column heading to sort."
              />
              <Select
                aria-label="Filter by outcome"
                value={outcome}
                onChange={(event) => setOutcome(event.target.value as "all" | "valid" | "invalid")}
              >
                <option value="all">All outcomes</option>
                <option value="valid">Valid only</option>
                <option value="invalid">Invalid only</option>
              </Select>
            </div>
            <Card className="crm-table-card">
              <table className="crm-table" aria-label="Lead generation history">
                <thead>
                  <tr>
                    {HISTORY_COLUMNS.map((column) => (
                      <SortHeader key={column.key} column={column} sort={historyTable.sort} onSort={historyTable.toggle} />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {historyTable.visible.length === 0 && (
                    <tr>
                      <td colSpan={HISTORY_COLUMNS.length} className="crm-muted">No records match this filter.</td>
                    </tr>
                  )}
                  {historyTable.visible.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <strong>{row.name}</strong>
                        <div className="crm-muted">{row.category ?? ""}</div>
                      </td>
                      <td className="leadgen-nowrap">{row.phone}</td>
                      <td>{row.email}</td>
                      <td>
                        {row.status === "valid" ? (
                          row.lead_id ? <Link to={crmHref(`/leads/${row.lead_id}`)}>Valid — lead created</Link> : "Valid"
                        ) : (
                          "Invalid"
                        )}
                      </td>
                      <td>{row.processed_by_email ?? "—"}</td>
                      <td>{row.processed_at ? formatDateTime(row.processed_at) : "—"}</td>
                      <td>{formatDateTime(row.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </Stack>
        ))}

      {convertTarget && (
        <Modal isOpen title="Add as lead" onClose={() => setConvertTarget(null)} width={720}>
          <Stack gap={3}>
            <p className="crm-muted">
              This business will be added as a lead. Check the details, then confirm.
            </p>
            {convertTarget.company && (
              <p className="crm-muted" role="status">
                {convertTarget.company.legal_name} already exists as a company, so the lead is linked to it instead of
                creating a duplicate.
              </p>
            )}
            <LeadCaptureForm
              orgId={orgId}
              lockedCompany={
                convertTarget.company ? { id: convertTarget.company.id, name: convertTarget.company.legal_name } : undefined
              }
              initial={{
                company_name: convertTarget.company?.legal_name ?? convertTarget.result.name,
                company_id: convertTarget.company?.id ?? "",
                email: convertTarget.result.email,
                phone: convertTarget.result.phone,
                lead_source: "outbound",
                rating: "warm",
                notes: [
                  `Found by LeadGen on ${formatDateTime(convertTarget.result.created_at)}.`,
                  convertTarget.result.fit_reason ? `Fit ${convertTarget.result.fit_score}: ${convertTarget.result.fit_reason}` : "",
                  convertTarget.result.call_opener ? `Opener: ${convertTarget.result.call_opener}` : "",
                  convertTarget.result.website ? `Website: ${convertTarget.result.website}` : "",
                  convertTarget.result.address ? `Address: ${convertTarget.result.address}` : "",
                ]
                  .filter(Boolean)
                  .join(" "),
              }}
              submitLabel="OK — add as lead"
              submit={submitConvert}
              onCreated={() => {
                setConvertTarget(null);
                show("Added as a lead.", "success");
                void loadResults(canConfigure);
              }}
            />
          </Stack>
        </Modal>
      )}

      {emailDraft && (
        <Modal isOpen title={`Email for ${emailDraft.business}`} onClose={() => setEmailDraft(null)} width={640}>
          <Stack gap={3}>
            <p className="crm-muted">
              Written for this first contact from your sales pitch and this business. Copy it into your email and send it yourself.
            </p>
            <p>
              <strong>Subject: </strong>
              {emailDraft.subject}
            </p>
            <pre className="leadgen-email" data-testid="leadgen-email-body">{emailDraft.body}</pre>
            <Cluster gap={2}>
              <Button onClick={() => void copyEmail()}>Copy email</Button>
              <Button variant="secondary" onClick={() => setEmailDraft(null)}>Close</Button>
            </Cluster>
          </Stack>
        </Modal>
      )}
    </ListWorkspace>
  );
}
